import { SectionBox } from '@shared/components/ui/SectionBox';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderPreparation } from '../../models/Order';

interface OrderProgressProps {
  orderCreatedAt?: string;
  readyToPrepareAt?: string | null;
  processedAt?: string | null;
  showPreparation?: boolean;
  preparation?: OrderPreparation;
}

interface Milestone {
  key: string;
  label: string;
  date: string | null | undefined;
}

type MilestoneStatus = 'completed' | 'upcoming';

const getLastCompletedIndex = (milestones: Milestone[]): number =>
  milestones.reduce((last, milestone, index) => (milestone.date ? index : last), -1);

const ProgressDot: FC<{ status: MilestoneStatus }> = ({ status }) => (
  <div
    className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
      status === 'completed' ? 'border-success bg-success text-white' : 'border-card-border bg-card-bg'
    }`}
  >
    {status === 'completed' && <FontAwesomeIcon icon={faCheck} className="!h-3 !w-3" />}
  </div>
);

const OrderProgressStrip: FC<{ milestones: Milestone[] }> = ({ milestones }) => {
  const lastCompletedIndex = getLastCompletedIndex(milestones);
  const progressPercent =
    milestones.length > 1 && lastCompletedIndex >= 0 ? (lastCompletedIndex / (milestones.length - 1)) * 100 : 0;
  const edgeOffset = `${100 / (2 * milestones.length)}%`;

  const getStatus = (index: number): MilestoneStatus => (milestones[index].date ? 'completed' : 'upcoming');

  return (
    <div className="relative" data-testid="order-progress">
      <div
        className="absolute top-4 h-px overflow-hidden rounded-full bg-lines"
        style={{ left: edgeOffset, right: edgeOffset }}
      >
        <div
          className="h-full rounded-full bg-success transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className={`grid gap-2 ${milestones.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {milestones.map((milestone, index) => {
          const status = getStatus(index);
          return (
            <div key={milestone.key} className="flex min-w-0 flex-col items-center px-1 text-center">
              <ProgressDot status={status} />
              <p
                className={`mt-2 w-full text-sm leading-tight ${
                  status === 'completed' ? 'text-complementary-text' : 'text-subtext'
                }`}
              >
                {milestone.label}
              </p>
              <p className="mt-0.5 h-4 w-full text-xs font-medium leading-4 tabular-nums text-title">
                {milestone.date ? (
                  formatDateWithTime(milestone.date)
                ) : (
                  <span className="font-normal text-subtext">—</span>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const OrderProgress: FC<OrderProgressProps> = ({
  orderCreatedAt,
  readyToPrepareAt,
  processedAt,
  showPreparation = false,
  preparation,
}) => {
  const { t } = useTranslation('viewOrdersModule');

  const resolvedReadyToPrepareAt = useMemo(
    () => readyToPrepareAt ?? (processedAt ? processedAt : null),
    [readyToPrepareAt, processedAt],
  );

  const resolvedInPreparationAt = useMemo(
    () => preparation?.created_at ?? (processedAt ? processedAt : null),
    [preparation?.created_at, processedAt],
  );

  const milestones: Milestone[] = useMemo(() => {
    const steps: Milestone[] = [
      { key: 'created', label: t('sections.order-info.creation-date'), date: orderCreatedAt },
      { key: 'ready', label: t('sections.order-info.ready-to-prepare-at'), date: resolvedReadyToPrepareAt },
    ];

    if (showPreparation) {
      steps.push({
        key: 'in-preparation',
        label: t('sections.order-info.in-preparation-at'),
        date: resolvedInPreparationAt,
      });
    }

    steps.push({ key: 'processed', label: t('sections.order-info.processed-at'), date: processedAt });

    return steps;
  }, [orderCreatedAt, resolvedReadyToPrepareAt, resolvedInPreparationAt, processedAt, showPreparation, t]);

  return (
    <SectionBox className="bg-card-bg" data-testid="order-progress-section">
      <OrderProgressStrip milestones={milestones} />
    </SectionBox>
  );
};
